import React from "react";
import FlexBox from "../../../FlexBox/FlexBox";
import Typography from "../../../../Foundation/Typography/Typography";
import { ImageResponsive } from "../../../Image";
import { ImageFit } from "../../../Image/interface/ImageData";
import Button from "../../../../Foundation/Button/Button";
import StyledDiv from "../../../StyledDiv/StyledDiv";
import { defaultTheme } from "../../../../Themes/defaultTheme";
import HighlightProductCardDecorationBase, {
  IHighlightProductCardDecorationBaseProps,
} from "./HighlightProductCardDecorationBase/HighlightProductCardDecorationBase";
import FadeIn from "../../../Animations/FadeIn";

export interface IHighlightProductCardData<T extends any = any> {
  id_product: string;
  name: string;
  price?: string;
  imageId: string;
  displayFromMention?: string;
  ctaLabel?: string;
  decorationVariant?: IHighlightProductCardDecorationBaseProps["variant"];
}

export interface IHighlightProductCardProps<T extends any = any> {
  product: IHighlightProductCardData<T>;
  className?: string;
  onClick?: (product: IHighlightProductCardData<T>) => void;
}

const HighlightProductCard = <T extends any = any>(
  props: IHighlightProductCardProps<T>
) => {
  const { product, className, onClick } = props;
  const ctaLabel = product.ctaLabel || "Je commande";

  const desktopAspectRatio = 437 / 383;
  const mobileAspectRatio = 324 / 383;

  const textColor = "#162740";
  return (
    <StyledDiv
      as={FlexBox}
      direction="column"
      gap={10}
      className={className}
      styles={{
        height: "100%",
        alignItems: "center",
        position: "relative",
      }}
    >
      {/* Product Image */}

      <StyledDiv
        styles={{
          width: "100%",
          borderRadius: 0,
          // overflow: "hidden",
          backgroundColor: "#FFFFFF",
          aspectRatio: desktopAspectRatio.toString(),
          marginBottom: 31 - 10,
          position: "relative",
          mobile: {
            aspectRatio: mobileAspectRatio.toString(),
            ".ro-image-responsive": {
              height: "100%",
            },
          },
        }}
      >
        {product.imageId && product.imageId !== "-1" && (
          <ImageResponsive
            alt={product.name}
            desktop={{
              image: {
                id: product.imageId,
                fit: ImageFit.COVER,
                quality: 80,
                width: 600,
                aspectRatio: desktopAspectRatio,
              },
              wrapperStyle: {
                width: "100%",
                height: "100%",
                borderRadius: 0,
                overflow: "hidden",
              },
            }}
            mobile={{
              image: {
                id: product.imageId,
                fit: ImageFit.COVER,
                quality: 100,
                outputPixelDensities: [1, 2],
                width: 400,
                aspectRatio: mobileAspectRatio,
              },
              wrapperStyle: {
                width: "100%",
                height: "100%",
                borderRadius: 0,
                overflow: "hidden",
              },
              imgStyle: {
                objectPosition: "center",
                height: "100%",
              },
            }}
          />
        )}
        <HighlightProductCardDecorationBase
          variant={product.decorationVariant}
        />
      </StyledDiv>

      {/* Product Name */}
      <FadeIn delay={0.1}>
        <Typography
          variant="h6"
          style={{ color: textColor, textAlign: "center" }}
        >
          {product.name}
        </Typography>
      </FadeIn>

      {/* Price */}
      {product.price && (
        <FadeIn delay={0.2}>
          <FlexBox direction="column" gap={4} align="center">
            {product.displayFromMention && (
              <Typography
                variant="ui4"
                style={{ color: textColor, textAlign: "center" }}
              >
                {product.displayFromMention}
              </Typography>
            )}
            <Typography
              variant="subtitle2"
              style={{
                color: textColor,
                fontSize: "15px",
                fontStyle: "normal",
                fontWeight: 800,
                lineHeight: "140%",
                textAlign: "center",
              }}
            >
              {product.price}
            </Typography>
          </FlexBox>
        </FadeIn>
      )}

      {/* Button */}
      <FadeIn delay={0.2}>
        <Button
          variant="champagne"
          style={{
            width: "fit-content",
          }}
          customize={{
            bg: defaultTheme.color.petitPrinceHighlightTitle,
            color: "#282722",
            border: defaultTheme.color.petitPrinceHighlightTitle,
            hoverBg: defaultTheme.color.petitPrinceHighlightTitle + "CC",
          }}
          onClick={() => onClick?.(product)}
        >
          {ctaLabel}
        </Button>
      </FadeIn>
    </StyledDiv>
  );
};

export default HighlightProductCard;
